import { useEffect } from "react";
import { setBreadcrumbsStore } from "./breadcrumbStore";

const DOMAIN = "https://www.vitadahost.it";
const DEFAULT_IMAGE =
  "https://customer-assets.emergentagent.com/job_vita-portal/artifacts/gd9ty91g_banner%20vdh%20nuovo.png";

function upsert(selector, create) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  return el;
}

function setMeta(name, content) {
  if (content === undefined || content === null) return;
  const el = upsert(`meta[name="${name}"]`, () => {
    const m = document.createElement("meta");
    m.setAttribute("name", name);
    return m;
  });
  el.setAttribute("content", String(content));
}

function setProp(property, content) {
  if (content === undefined || content === null) return;
  const el = upsert(`meta[property="${property}"]`, () => {
    const m = document.createElement("meta");
    m.setAttribute("property", property);
    return m;
  });
  el.setAttribute("content", String(content));
}

function setLink(rel, href, extra = {}) {
  // Make selector hreflang-aware so multiple <link rel="alternate"> entries can coexist
  const attrSelector = Object.entries(extra)
    .map(([k, v]) => `[${k}="${v}"]`)
    .join("");
  const el = upsert(`link[rel="${rel}"]${attrSelector}`, () => {
    const m = document.createElement("link");
    m.setAttribute("rel", rel);
    Object.entries(extra).forEach(([k, v]) => m.setAttribute(k, v));
    return m;
  });
  el.setAttribute("href", href);
  Object.entries(extra).forEach(([k, v]) => el.setAttribute(k, v));
}

function setJsonLd(id, data) {
  let el = document.head.querySelector(`script[data-ld="${id}"]`);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-ld", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

/**
 * useSEO — SEO + AI-indexing 360°
 *
 * @param {Object} opts
 * @param {string} opts.title           Page title (55-65 char sweet spot)
 * @param {string} opts.description     Meta description (140-160 char)
 * @param {string} opts.keywords        Comma-separated keywords
 * @param {string} opts.path            Path beginning with "/"
 * @param {string} [opts.image]         OG/Twitter hero image URL
 * @param {string} [opts.type]          og:type (website | article | product)
 * @param {Object} [opts.jsonLd]        Custom JSON-LD (replaces page schema)
 * @param {Array}  [opts.breadcrumbs]   [{name, path}] array for Breadcrumb schema
 * @param {Array}  [opts.faq]           [{q, a}] array for FAQPage schema
 * @param {Object} [opts.article]       {headline, author, datePublished, dateModified, image}
 * @param {Object} [opts.product]       {name, price, currency, availability, sku, brand}
 * @param {string} [opts.locale]        Locale (default it_IT)
 * @param {boolean}[opts.noindex]       Set robots noindex,nofollow
 */
export default function useSEO({
  title,
  description,
  socialDescription,
  keywords,
  path = "",
  image = DEFAULT_IMAGE,
  imageAlt,
  type = "website",
  jsonLd,
  breadcrumbs,
  faq,
  article,
  product,
  locale = "it_IT",
  noindex = false,
} = {}) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) setMeta("description", description);
    if (keywords) setMeta("keywords", keywords);

    // Robots — comprehensive (main + named bots)
    const robots = noindex
      ? "noindex, nofollow"
      : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";
    setMeta("robots", robots);
    setMeta("googlebot", robots);
    setMeta("bingbot", robots);
    // AI / LLM crawlers — explicit allow + curation signal
    setMeta("ai-content-declaration", "human-curated");
    setMeta("content-language", "it-IT");
    setMeta("language", "it");
    setMeta("author", "Vita da Host");
    setMeta("publisher", "Vita da Host");
    setMeta("theme-color", "#050505");
    setMeta("format-detection", "telephone=no");

    const canonical = `${DOMAIN}${path}`;
    setLink("canonical", canonical);
    setLink("alternate", canonical, { hreflang: "it-it" });
    setLink("alternate", canonical, { hreflang: "x-default" });

    // Google Analytics 4 — SPA page_view per route change.
    // gtag.js is loaded once in index.html with send_page_view: true (initial).
    // For subsequent client-side navigations we manually fire page_view here.
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: path || window.location.pathname,
        page_location: canonical,
        page_title: title || document.title,
      });
    }

    // OpenGraph
    const social = socialDescription || description || "";
    setProp("og:title", title || "Vita da Host");
    setProp("og:description", social);
    setProp("og:url", canonical);
    setProp("og:site_name", "Vita da Host");
    setProp("og:type", type);
    setProp("og:locale", locale);
    setProp("og:image", image);
    setProp("og:image:alt", imageAlt || title || "Vita da Host");
    setProp("og:image:width", "1200");
    setProp("og:image:height", "630");

    // Twitter / X
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title || "Vita da Host");
    setMeta("twitter:description", social);
    setMeta("twitter:image", image);
    setMeta("twitter:image:alt", imageAlt || title || "Vita da Host");
    setMeta("twitter:site", "@vitadahost");
    setMeta("twitter:creator", "@vitadahost");

    // Article-specific OG tags
    if (article) {
      setProp("article:author", article.author || "Vita da Host");
      if (article.datePublished) setProp("article:published_time", article.datePublished);
      if (article.dateModified) setProp("article:modified_time", article.dateModified);
      setProp("article:section", article.section || "Affitti brevi");
      (article.tags || []).forEach((t, i) => {
        const el = upsert(`meta[property="article:tag"][data-idx="${i}"]`, () => {
          const m = document.createElement("meta");
          m.setAttribute("property", "article:tag");
          m.setAttribute("data-idx", String(i));
          return m;
        });
        el.setAttribute("content", t);
      });
    }

    // ======= JSON-LD =======
    // Always inject a base Organization/WebPage node unless overridden
    if (!jsonLd) {
      setJsonLd("page", {
        "@context": "https://schema.org",
        "@type": type === "article" ? "Article" : type === "product" ? "Product" : "WebPage",
        "@id": `${canonical}#page`,
        url: canonical,
        name: title,
        headline: title,
        description,
        inLanguage: "it-IT",
        isPartOf: { "@id": `${DOMAIN}/#website` },
        publisher: { "@id": `${DOMAIN}/#organization` },
        primaryImageOfPage: { "@type": "ImageObject", url: image },
        ...(article
          ? {
              author: {
                "@type": "Person",
                name: article.author || "Vita da Host",
                url: DOMAIN,
              },
              datePublished: article.datePublished,
              dateModified: article.dateModified || article.datePublished,
              image: article.image || image,
              mainEntityOfPage: canonical,
            }
          : {}),
        ...(product
          ? {
              "@type": "Product",
              name: product.name || title,
              description,
              image: product.image || image,
              sku: product.sku,
              brand: { "@type": "Brand", name: product.brand || "Vita da Host" },
              offers: {
                "@type": "Offer",
                url: canonical,
                priceCurrency: product.currency || "EUR",
                price: product.price,
                availability:
                  product.availability ||
                  "https://schema.org/InStock",
                seller: { "@id": `${DOMAIN}/#organization` },
              },
            }
          : {}),
      });
    } else {
      setJsonLd("page", jsonLd);
    }

    // Breadcrumbs
    if (breadcrumbs && breadcrumbs.length > 0) {
      setJsonLd("breadcrumbs", {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((b, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: b.name,
          item: b.path?.startsWith("http") ? b.path : `${DOMAIN}${b.path || ""}`,
        })),
      });
    }
    setBreadcrumbsStore(breadcrumbs && breadcrumbs.length > 0 ? breadcrumbs : []);

    // FAQ
    if (faq && faq.length > 0) {
      setJsonLd("faq", {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      });
    }
  }, [
    title,
    description,
    socialDescription,
    keywords,
    path,
    image,
    imageAlt,
    type,
    jsonLd,
    breadcrumbs,
    faq,
    article,
    product,
    locale,
    noindex,
  ]);
}
